import axios from 'axios';
import { request } from '../../helpers/requests';
import { parseAndUploadFiles } from '../../files/fileHelpers';
import { formatPayloadCustomData } from '../../equipment/state/equipment.service';

// users/vendors -> user/vendor; equipment stays equipment
const toSingularEntityType = (entityType) => (
  entityType?.endsWith('s') ? entityType.slice(0, -1) : entityType
);

const formatCertificationPayload = async (payload, files) => {
  const fullPayload = { ...payload };
  const {
    customData: formattedCustomData,
    files: formattedFiles,
  } = await formatPayloadCustomData(payload.customData);
  fullPayload.customData = formattedCustomData;

  const parsedFiles = await parseAndUploadFiles(files);
  fullPayload.files = parsedFiles.concat(
    formattedFiles.map((file) => ({ ...file, isFormFile: true })),
  );
  return fullPayload;
};

const CertificationService = {
  getAllCertifications: (entityType) => request({
    call: axios.get(`/${entityType}/certifications`, { params: { getCustomData: true } }),
    errMsg: 'Could not get certifications',
    hideSuccessToast: true,
  }),
  getAllCertificationsForEntity: (entityType, id) => request({
    call: axios.get(`/${entityType}/${id}/certifications`, { params: { getCustomData: true } }),
    hideSuccessToast: true,
    errMsg: `Could not get ${toSingularEntityType(entityType)} certifications`,
  }),
  getCertification: (entityType, id, certificationId) => request({
    call: axios.get(`/${entityType}/${id}/certifications/${certificationId}`),
    errMsg: `Could not get ${toSingularEntityType(entityType)} certification`,
    hideSuccessToast: true,
  }),
  createCertification: (entityType, id, payload, files) => request({
    call: async () => {
      const formattedPayload = await formatCertificationPayload(payload, files);
      return axios.post(`/${entityType}/${id}/certifications`, formattedPayload, { params: { getCustomData: true } });
    },
    errMsg: `Could not create ${toSingularEntityType(entityType)} certification`,
  }),
  updateCertification: (entityType, id, certificationId, payload, files) => request({
    call: async () => {
      const formattedPayload = await formatCertificationPayload(payload, files);
      return axios.put(`/${entityType}/${id}/certifications/${certificationId}`, formattedPayload, { params: { getCustomData: true } });
    },
    errMsg: `Could not update ${toSingularEntityType(entityType)} certification`,
  }),
  deleteCertification: (entityType, id, certificationId) => request({
    call: axios.delete(`/${entityType}/${id}/certifications/${certificationId}`, { params: { getCustomData: true } }),
    errMsg: `Could not delete ${toSingularEntityType(entityType)} certification`,
  }),
  getCertificationCustomFieldTemplate: (entityType) => request({
    call: axios.get(`/${toSingularEntityType(entityType)}certifications/customFieldTemplate`),
    errMsg: `Failed to get ${toSingularEntityType(entityType)} certification custom field template`,
    hideSuccessToast: true,
  }),
  updateCertificationCustomFieldTemplate: (entityType, payload) => request({
    call: axios.post(`/${toSingularEntityType(entityType)}certifications/customFieldTemplate`, payload),
    errMsg: `Failed to update ${toSingularEntityType(entityType)} certification custom field template`,
  }),
  getCertificationCustomData: (entityType, id) => request({
    call: axios.get(`/${toSingularEntityType(entityType)}certifications/${id}/data`, { params: { getSignedUrls: false } }),
    errMsg: `Failed to get ${toSingularEntityType(entityType)} certification custom data`,
    hideSuccessToast: true,
  }),
  getCertificationTypes: (entityType) => request({
    call: axios.get('/certificationTypes', { params: { entityType } }),
    errMsg: 'Could not get certification types',
    hideSuccessToast: true,
  }),
  createCertificationType: (entityType, payload) => request({
    call: axios.post('/certificationTypes', { ...payload, entityType }),
    errMsg: 'Could not create certification type',
  }),
  updateCertificationType: (id, payload) => request({
    call: axios.put(`/certificationTypes/${id}`, payload),
    errMsg: 'Could not update certification type',
  }),
  deleteCertificationType: (id) => request({
    call: axios.delete(`/certificationTypes/${id}`),
    errMsg: 'Could not delete certification type',
  }),
};

export default CertificationService;
